// contexts/themeContext.jsx
import { createContext, useState, useEffect, useCallback, useMemo } from "react";

export const colors = {
  light: {
    mainBg: "#EAF3F9",
    cardBg: "#F7FBFE",
    raisedBg: "#FFFFFF",
    subtleBg: "#EEF5FA",

    text: "#2A3755",
    heading: "#0A0F1E",
    secondaryText: "#5C7A9A",

    btnBg: "#1A6FD4",
    btnText: "#FFFFFF",
    btnHover: "#155BB0",
    linkColor: "#1A6FD4",
    hoverBg: "#E8F4FB",
    activeBg: "rgba(26, 111, 212, 0.10)",

    border: "#D3E4EF",
    borderStrong: "#A9C9DF",

    success: "#16A34A",
    successBg: "#DCFCE7",
    error: "#DC2626",
    errorBg: "#FEE2E2",
    warning: "#D97706",
    warningBg: "#FEF3C7",

    glassBg: "rgba(255, 255, 255, 0.72)",
    glassBorder: "rgba(211, 228, 239, 0.9)",

    shadow: "rgba(10, 15, 30, 0.08)",
    shadowStrong: "rgba(10, 15, 30, 0.16)",
    scrim: "rgba(10, 15, 30, 0.45)",
  },
  dark: {
    mainBg: "#0A0F1E",
    cardBg: "#111827",
    raisedBg: "#141E33",
    subtleBg: "#0F1729",

    text: "#A8C4D8",
    heading: "#E4F0F6",
    secondaryText: "#5C8AAA",

    btnBg: "#4DA6E8",
    btnText: "#0A0F1E",
    btnHover: "#6FB8F0",
    linkColor: "#4DA6E8",
    hoverBg: "#162035",
    activeBg: "rgba(77, 166, 232, 0.14)",

    border: "#1E3050",
    borderStrong: "#2A3F60",

    success: "#4ADE80",
    successBg: "#052814",
    error: "#F87171",
    errorBg: "#2D0A0A",
    warning: "#FCD34D",
    warningBg: "#422006",

    glassBg: "rgba(20, 30, 51, 0.72)",
    glassBorder: "rgba(30, 48, 80, 0.9)",

    shadow: "rgba(0, 0, 0, 0.5)",
    shadowStrong: "rgba(0, 0, 0, 0.7)",
    scrim: "rgba(0, 0, 0, 0.6)",
  },
};

export const ThemeContext = createContext();

const TOKEN_KEYS = Object.keys(colors.light);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({ mode: "light", system: true });
  const [isThemeReady, setIsThemeReady] = useState(false);

  const getSystemTheme = () =>
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";

  const applyTokens = useCallback((mode) => {
    const root = document.documentElement;
    const current = colors[mode];
    TOKEN_KEYS.forEach((key) => {
      root.style.setProperty(`--${key}`, current[key]);
    });
    root.setAttribute("data-theme", mode);
    root.style.colorScheme = mode;
  }, []);

  const updateTheme = useCallback(
    (newTheme) => {
      let next = newTheme;
      let mode = next?.mode;

      if (!next) {
        mode = theme.mode === "dark" ? "light" : "dark";
        next = { mode, system: false };
      } else if (next.system) {
        mode = getSystemTheme();
        next = { mode, system: true };
      }

      setTheme(next);
      try {
        localStorage.setItem("woodaxis_theme", JSON.stringify(next));
      } catch {}
      applyTokens(mode);
    },
    [theme.mode, applyTokens]
  );

  useEffect(() => {
    let initial;
    try {
      const stored = localStorage.getItem("woodaxis_theme");
      initial = stored
        ? JSON.parse(stored)
        : { mode: getSystemTheme(), system: true };
    } catch {
      initial = { mode: getSystemTheme(), system: true };
    }

    setTheme(initial);
    applyTokens(initial.mode);
    setIsThemeReady(true);

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      setTheme((prev) => {
        if (!prev.system) return prev;
        const mode = e.matches ? "dark" : "light";
        applyTokens(mode);
        return { mode, system: true };
      });
    };

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [applyTokens]);

  const value = useMemo(
    () => ({
      theme,
      updateTheme,
      isThemeReady,
      colors: colors[theme.mode],
    }),
    [theme, updateTheme, isThemeReady]
  );

  return (
    <ThemeContext.Provider value={value}>
      {isThemeReady ? (
        children
      ) : (
        <div style={{ background: "#EAF3F9", height: "100vh" }} />
      )}
    </ThemeContext.Provider>
  );
};